/* === src/pages/admin/ReviewsManagementPage.js (Fixed with Timeout) === */
import React, { useState, useEffect } from 'react';
import { 
    Box, Container, Typography, Paper, Chip, 
    IconButton, TextField, InputAdornment, Tooltip, Alert
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { 
    CheckCircle as ApproveIcon, 
    Cancel as RejectIcon, 
    Search as SearchIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import api from '../../services/api';

const ReviewsManagementPage = () => {
    const [reviews, setReviews] = useState([]);
    const [filteredReviews, setFilteredReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // --- 1. Fetch Data ---
    const fetchReviews = async () => {
        setLoading(true);
        setErrorMsg('');

        // SAFETY TIMEOUT: Force stop loading after 10 seconds if backend hangs
        const timeoutId = setTimeout(() => {
            setLoading((currentLoading) => {
                if (currentLoading) {
                    setErrorMsg("Request timed out. Backend might be unreachable.");
                    return false;
                }
                return false;
            });
        }, 10000);

        try {
            const { data } = await api.get('/admin/reviews'); 
            
            if (Array.isArray(data)) {
                const safeReviews = data.map((row) => ({
                    ...row,
                    id: row.Id, // FIX: Maps 'Id' to lowercase 'id' for MUI
                    AuthorName: row.AuthorName || 'Unknown User'
                }));
                setReviews(safeReviews);
                setFilteredReviews(safeReviews);
            } else {
                setReviews([]);
                setFilteredReviews([]);
            }
        } catch (error) {
            console.error("Failed to load reviews:", error);
            setErrorMsg("Failed to load reviews. Check console.");
        } finally {
            clearTimeout(timeoutId); // Clear the safety timer
            setLoading(false);       // Stop the spinner
        }
    };

    useEffect(() => {
        fetchReviews();
    }, []);

    // --- 2. Search Filter ---
    useEffect(() => {
        const lowerTerm = searchTerm.toLowerCase();
        if (!lowerTerm) {
            setFilteredReviews(reviews);
            return;
        }
        
        const filtered = reviews.filter(r => 
            (r.AuthorName && r.AuthorName.toLowerCase().includes(lowerTerm)) ||
            (r.ReviewText && r.ReviewText.toLowerCase().includes(lowerTerm)) ||
            (r.Role && r.Role.toLowerCase().includes(lowerTerm))
        );
        setFilteredReviews(filtered);
    }, [searchTerm, reviews]);

    // --- 3. Handlers ---
    const handleStatusChange = async (id, newStatus) => {
        // Optimistic Update
        const oldReviews = [...reviews];
        setReviews(prev => prev.map(r => r.id === id ? { ...r, Status: newStatus } : r));
        
        try {
            await api.put(`/admin/reviews/${id}/status`, { status: newStatus });
        } catch (error) {
            console.error("Update failed", error);
            setReviews(oldReviews); // Revert
            alert("Failed to update status");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this review?")) return;
        try {
            setReviews(prev => prev.filter(r => r.id !== id));
            await api.delete(`/admin/reviews/${id}`);
        } catch (error) {
            console.error("Delete failed", error);
            alert("Failed to delete review");
            fetchReviews();
        }
    };

    const columns = [
        { field: 'id', headerName: 'ID', width: 70 },
        { field: 'AuthorName', headerName: 'Author', width: 180 },
        { field: 'Role', headerName: 'Role', width: 120 },
        { 
            field: 'Rating', headerName: 'Rating', width: 100,
            renderCell: (params) => <Box>{"⭐".repeat(params.value)}</Box>
        },
        { field: 'ReviewText', headerName: 'Review', flex: 1, minWidth: 250 },
        { 
            field: 'Status', headerName: 'Status', width: 130,
            renderCell: (params) => (
                <Chip 
                    label={params.value} 
                    color={params.value === 'Approved' ? 'success' : params.value === 'Rejected' ? 'error' : 'warning'}
                    size="small"
                />
            )
        },
        {
            field: 'actions', headerName: 'Actions', width: 150, sortable: false,
            renderCell: (params) => (
                <Box>
                    <Tooltip title="Approve">
                        <IconButton color="success" size="small" onClick={() => handleStatusChange(params.row.id, 'Approved')}><ApproveIcon /></IconButton>
                    </Tooltip>
                    <Tooltip title="Reject">
                        <IconButton color="warning" size="small" onClick={() => handleStatusChange(params.row.id, 'Rejected')}><RejectIcon /></IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton color="error" size="small" onClick={() => handleDelete(params.row.id)}><DeleteIcon /></IconButton>
                    </Tooltip>
                </Box>
            )
        }
    ];

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', height: 700 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" component="h2">Reviews Management</Typography>
                    <TextField 
                        size="small" placeholder="Search..." value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                    />
                </Box>

                {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}

                <DataGrid
                    rows={filteredReviews}
                    columns={columns}
                    loading={loading}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    pageSizeOptions={[10, 25, 50]}
                    disableRowSelectionOnClick
                    slots={{ toolbar: GridToolbar }}
                />
            </Paper>
        </Container>
    );
};

export default ReviewsManagementPage;